import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi, usePatchApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { TaskSet, TaskSetForm, TaskSetSearchForm } from 'entities/task_set'
import { TaskMasterSetKind } from 'entities/task_master_set'
import { TaskGroup } from 'entities/task_group'
import { Institution } from 'entities/institution'

type TaskSetsResponse = PagingResponse & {
    results: TaskSet[]
}

export function useFetchTaskSetsApi(searchForm?: Form<TaskSetSearchForm>): IndexApiSet<TaskSetsResponse> & { execute: () => void } {
    const apiPath = 'task_sets/'
    const api = useIndexApi<TaskSetsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchTaskSetApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `task_sets/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostTaskSetApi(): ApiSet<BaseResponse> & { execute: (form: Form<TaskSetForm>) => void } {
    const apiSet = usePostApi<BaseResponse, TaskSetForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<TaskSetForm>) => {
        const apiPath = `task_sets/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutTaskSetApi(): ApiSet<BaseResponse> & { execute: (form: Form<TaskSetForm>) => void } {
    const apiSet = usePutApi<BaseResponse, TaskSetForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<TaskSetForm>) => {
        const apiPath = `task_sets/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutTaskSetsApi(): ApiSet<BaseResponse> & { execute: (form: Form<TaskSetForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, TaskSetForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TaskSetForm[]>) => {
        const apiPath = `task_sets/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteTaskSetApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `task_sets/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function useCompleteTaskSetApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = usePatchApi<BaseResponse, TaskSetForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `task_sets/${id}/complete/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function useCancelTaskSetApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = usePatchApi<BaseResponse, TaskSetForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `task_sets/${id}/cancel/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkCompleteTaskSetApi(): ApiSet<BaseResponse> & { execute: (params: TaskSetForm) => void } {
    const apiSet = usePatchApi<BaseResponse, TaskSetForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (params: TaskSetForm) => {
        const apiPath = `task_sets/bulk_complete/`
        apiSet.execute(apiPath, params)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkCancelTaskSetApi(): ApiSet<BaseResponse> & { execute: (params: { taskSetIds: number[] }) => void } {
    const apiSet = usePatchApi<BaseResponse, { taskSetIds: number[] }>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (params: { taskSetIds: number[] }) => {
        const apiPath = `task_sets/bulk_cancel/`
        apiSet.execute(apiPath, params)
    }

    return { ...apiSet, execute: execute }
}

export type CreateTaskGroupForm = {
    taskSetIds?: number[]
    kind?: TaskMasterSetKind
}

type CreateTaskGroupWithTaskSetsResponse = BaseResponse & {
    taskGroup?: TaskGroup
}

export function useCreateTaskGroupWithTaskSetsApi(): ApiSet<CreateTaskGroupWithTaskSetsResponse> & { execute: (form: Form<CreateTaskGroupForm>) => void } {
    const apiSet = usePostApi<CreateTaskGroupWithTaskSetsResponse, CreateTaskGroupForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<CreateTaskGroupForm>) => {
        const apiPath = `task_sets/create_group/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export type CreateMaterialTaskForm = {
    kind?: TaskMasterSetKind
    userGroupId?: number
    institutions?: Institution[]
    taskMasterSetId?: number
    taskDataList?: { taskMasterId?: number; number?: number; materialName?: string; materialId?: number }[]
    additionalTasks?: { materialId?: number; materialName?: string; number?: number; url?: string }[]
    materialTasks?: { materialInfo?: string; number?: number }[]
    institutionTasks?: {
        institutionId?: number
        institutionName?: string
        taskInfo?: { taskMasterId?: number; number?: number; materialName?: string; url?: string; isAdditional?: boolean }[]
    }[]
    institutionMaterials?: { [key: number]: string[] }
}

export function useCreateTasksFromTaskMasterSetApi(): ApiSet<BaseResponse> & { execute: (form: Form<CreateMaterialTaskForm>) => void } {
    const apiSet = usePostApi<BaseResponse, CreateMaterialTaskForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<CreateMaterialTaskForm>) => {
        const apiPath = `task_sets/create_tasks_by_master/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}
